import {
  computed, ref, watch,
} from 'vue';
import type { Store } from 'vuex';
import type { FetchUserOrganizationsResult } from '@/views/web-app/dashboard/gql-request';

export interface StickerHostTarget {
  entity?: string;
  entityId?: string;
  organization?: { id?: string };
  user?: { id?: string };
}

export interface HostOrganizationsLoadMeta {
  source: string;
  error?: string;
  graphqlErrors?: string[];
  organizationCount?: number;
}

function normalizeOrgId(id: unknown): string | null {
  if (id == null || id === '') return null;
  return String(id);
}

function addOrganizationRecordIds(
  ids: Set<string>,
  org: { id?: unknown; orgId?: unknown } | null | undefined,
): void {
  const normalizedId = normalizeOrgId(org?.id ?? org?.orgId);
  if (normalizedId) ids.add(normalizedId);
}

function organizationsToIds(
  organizations: Array<{ id?: unknown; orgId?: unknown }>,
): string[] {
  const ids = new Set<string>();
  organizations.forEach((org) => addOrganizationRecordIds(ids, org));
  return Array.from(ids);
}

export function useStickerHostCheck(
  store: Store<any>,
  fetchUserOrganizations: () => Promise<FetchUserOrganizationsResult>,
) {
  const managedOrganizationIds = ref<string[]>([]);
  const hostCheckReady = ref(false);
  const hostContextTick = ref(0);
  const hostOrganizationsLoadMeta = ref<HostOrganizationsLoadMeta>({ source: 'none' });

  const me = computed(() => store.getters['users/me']);

  function buildHostOrganizationIdSet(): Set<string> {
    hostContextTick.value;

    const ids = new Set<string>();
    managedOrganizationIds.value.forEach((id) => {
      const normalizedId = normalizeOrgId(id);
      if (normalizedId) ids.add(normalizedId);
    });

    return ids;
  }

  const hostOrganizationIds = computed(() => {
    managedOrganizationIds.value;
    hostContextTick.value;

    return Array.from(buildHostOrganizationIdSet());
  });

  async function loadHostOrganizationIds(): Promise<void> {
    if (!me.value?.token && !me.value?.id) {
      managedOrganizationIds.value = [];
      return;
    }

    try {
      const result = await fetchUserOrganizations();
      hostOrganizationsLoadMeta.value = {
        source: result.source,
        error: result.error,
        graphqlErrors: result.graphqlErrors,
        organizationCount: result.organizationCount,
      };

      managedOrganizationIds.value = organizationsToIds(result.organizations);
    } catch (err) {
      managedOrganizationIds.value = [];
      hostOrganizationsLoadMeta.value = {
        source: 'error',
        error: (err as Error)?.message || String(err),
        organizationCount: 0,
      };
      console.error('Failed to load managed orgs for host check:', err);
    } finally {
      hostContextTick.value += 1;
    }
  }

  function isHostOfSticker(target: StickerHostTarget): boolean {
    if (!me.value) return false;

    if (target.entity === 'Organization') {
      const hostIds = buildHostOrganizationIdSet();
      const stickerOrgIds = [
        normalizeOrgId(target.entityId),
        normalizeOrgId(target.organization?.id),
      ].filter(Boolean) as string[];

      return stickerOrgIds.some((id) => hostIds.has(id));
    }

    return normalizeOrgId(me.value.id) === normalizeOrgId(target.user?.id);
  }

  function scheduleHostOrganizationRetries(): void {
    const retryDelays = [300, 1000, 2000];

    retryDelays.forEach((delay) => {
      window.setTimeout(() => {
        if (!managedOrganizationIds.value.length) {
          loadHostOrganizationIds();
        }
      }, delay);
    });
  }

  function finalizeHostCheck(): void {
    hostCheckReady.value = true;

    if (me.value) {
      scheduleHostOrganizationRetries();
    }
  }

  watch(
    () => [me.value?.id, me.value?.token],
    ([userId, token]) => {
      if (userId && token) {
        loadHostOrganizationIds();
      } else {
        managedOrganizationIds.value = [];
      }
    },
  );

  return {
    hostOrganizationIds,
    profileOrganizationIds: managedOrganizationIds,
    managedOrganizationIds,
    hostCheckReady,
    hostContextTick,
    hostOrganizationsLoadMeta,
    loadHostOrganizationIds,
    finalizeHostCheck,
    isHostOfSticker,
  };
}
